import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const SpaceGallery = ({ images, language }) => {
  const [t] = useTranslation('global');
  const [showAll, setShowAll] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const openImage = (image) => {
    setSelectedImage(image);
  };

  const closeImage = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    
    const currentIndex = images.indexOf(selectedImage);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % images.length;
    } else {
      newIndex = (currentIndex - 1 + images.length) % images.length;
    }
    
    setSelectedImage(images[newIndex]);
  };

  // Mobile slider navigation
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (distance > minSwipeDistance) {
      nextSlide(); // Swipe left
    }
    
    if (distance < -minSwipeDistance) {
      prevSlide(); // Swipe right
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Dot indicators for mobile slider
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className={styles.spaceGallery}>
      {/* Desktop View */}
      {!isMobile && (
        <div className={styles.galleryContainer}>
          {/* First image */}
          <div className={styles.verticalImage} onClick={() => openImage(images[0])}>
            <img src={images[0]} alt="Space view 1" />
          </div>

          {/* Second image */}
          <div className={styles.verticalImage} onClick={() => openImage(images[1])}>
            <img src={images[1]} alt="Space view 2" />
          </div>

          {/* Third image or view more button */}
          {images.length > 3 ? (
            <div className={styles.verticalImage}>
              <div className={styles.viewMore} onClick={() => setShowAll(true)}>
                <img 
                  src={images[3]} 
                  alt={`Space view 4`}
                  className={styles.backgroundImage}
                />
                <div className={styles.viewMoreContent}>
                  {t("space.gallery.viewMore", { count: images.length - 3 })}
                </div>
              </div>
            </div>
          ) : (
            images[2] && (
              <div className={styles.verticalImage} onClick={() => openImage(images[2])}>
                <img src={images[2]} alt="Space view 3" />
              </div>
            )
          )}
        </div>
      )}

      {/* Mobile View - Slider */}
      {isMobile && (
        <div className={styles.mobileGallery}>
          <div 
            className={styles.sliderContainer}
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className={styles.sliderTrack}
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {images.map((image, index) => (
                <div 
                  key={`${language}-mobile-${index}`}
                  className={styles.slide}
                  onClick={() => openImage(image)}
                >
                  <img src={image} alt={`Space view ${index + 1}`} />
                </div>
              ))}
            </div>
            
            {/* Navigation arrows for mobile */}
            <button 
              className={`${styles.sliderNav} ${styles.prevBtn}`}
              onClick={prevSlide}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button 
              className={`${styles.sliderNav} ${styles.nextBtn}`}
              onClick={nextSlide}
              aria-label="Next image"
            >
              ›
            </button>

            {/* Image counter */}
            <div className={styles.sliderCounter}>
              <span className={styles.currentSlide}>{currentSlide + 1}</span>
              <span className={styles.totalSlides}> / {images.length}</span>
            </div>

            {/* Dot indicators */}
            {images.length > 1 && (
              <div className={styles.dotsContainer}>
                {images.map((_, index) => (
                  <button
                    key={`dot-${index}`}
                    className={`${styles.dot} ${index === currentSlide ? styles.activeDot : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* View all photos button */}
            <div className={styles.viewAllButton} onClick={() => setShowAll(true)}>
              {t("space.gallery.viewAllPhotos", { count: images.length })}
            </div>
          </div>
        </div>
      )}

      {/* Full image view modal */}
      {selectedImage && (
        <div className={styles.imageModal}>
          <div className={styles.modalHeader}>
            <span className={styles.imageCounter}>
              {images.indexOf(selectedImage) + 1} / {images.length}
            </span>
            <button onClick={closeImage}>×</button>
          </div>
          <div className={styles.modalContent}>
            <button 
              className={styles.navButton} 
              onClick={() => navigateImage('prev')}
            >
              ‹
            </button>
            <img 
              src={selectedImage} 
              alt="Full size view" 
              onClick={closeImage}
            />
            <button 
              className={styles.navButton} 
              onClick={() => navigateImage('next')}
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* Gallery grid modal */}
      {showAll && (
        <div className={styles.galleryModal}>
          <div className={styles.modalHeader}>
            <h3>{t("space.gallery.allPhotosTitle", { count: images.length })}</h3>
            <button onClick={() => setShowAll(false)}>×</button>
          </div>
          <div className={styles.modalImages}>
            {images.map((image, index) => (
              <div 
                key={`${language}-full-${index}`}
                className={styles.gridImage}
                onClick={() => openImage(image)}
              >
                <img 
                  src={image} 
                  alt={`Space view ${index + 1}`} 
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceGallery;